import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  Paper,
  Switch,
  FormControlLabel,
  InputAdornment,
  CircularProgress,
  Alert,
  Divider,
  Chip,
  IconButton,
  Autocomplete,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import AdminRichTextEditor from '../components/AdminRichTextEditor';
import MultiImageUpload from '../components/MultiImageUpload';
import { adminAPI } from '../services/api';

export default function StoreProductForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState([]);
  const [allTreks, setAllTreks] = useState([]);

  // Form State
  const [form, setForm] = useState({
    name: '',
    categoryId: '',
    storeType: 'merchandise', // 'merchandise', 'camper_store', 'both'
    saleType: 'purchase', // 'purchase', 'rental', 'both'
    basePurchasePrice: '',
    baseRentalPrice: '',
    securityDeposit: '',
    selectedEvents: [], // Array of Trek objects
    eventPricing: {}, // { [trekId]: price }
    eventOffsets: {}, // { [trekId]: { issueOffset: 0, returnOffset: 4 } }
    description: '',
    specificationsList: [
      { key: '', value: '' }
    ],
    images: [],
    variantsList: ['Standard'], // Array of variant names (e.g. ['S', 'M', 'L', 'XL'])
    newVariantInput: '',
    isActive: true,
    isFeatured: false
  });

  const variantsListRef = useRef(null);

  // Initialize SortableJS on variants list for Drag & Drop reordering
  useEffect(() => {
    if (!variantsListRef.current) return;
    let sortableInstance = null;

    import('sortablejs').then((module) => {
      const Sortable = module.default;
      sortableInstance = Sortable.create(variantsListRef.current, {
        animation: 200,
        handle: '.variant-drag-handle',
        ghostClass: 'sortable-ghost',
        onEnd: (evt) => {
          if (evt.oldIndex === evt.newIndex) return;
          setForm((prev) => {
            const list = [...prev.variantsList];
            const [moved] = list.splice(evt.oldIndex, 1);
            list.splice(evt.newIndex, 0, moved);
            return { ...prev, variantsList: list };
          });
        }
      });
    });

    return () => {
      if (sortableInstance) sortableInstance.destroy();
    };
  }, [form.variantsList.length]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token') || localStorage.getItem('admin_token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        const [catRes, treksRes] = await Promise.all([
          axios.get('/api/v1/admin/store/categories', { headers }),
          axios.get('/api/v1/treks', { headers })
        ]);

        if (catRes.data.success) setCategories(catRes.data.data);
        const treksList = treksRes.data?.data || treksRes.data || [];
        setAllTreks(treksList);

        if (isEdit) {
          const prodRes = await axios.get(`/api/v1/admin/store/products/${id}`, { headers });
          if (prodRes.data.success) {
            const p = prodRes.data.data;

            // Normalize specifications into { key, value } list
            let specs = [];
            if (Array.isArray(p.specifications)) {
              specs = p.specifications.map((s) => {
                const str = String(s).trim();
                if (str.includes(':')) {
                  const parts = str.split(':');
                  return { key: parts[0].trim(), value: parts.slice(1).join(':').trim() };
                }
                return { key: 'Feature', value: str };
              });
            } else if (typeof p.specifications === 'object' && p.specifications !== null) {
              specs = Object.entries(p.specifications).map(([k, v]) => ({ key: k, value: String(v) }));
            }
            if (specs.length === 0) {
              specs = [{ key: '', value: '' }];
            }

            const vList = p.variants && p.variants.length > 0
              ? p.variants.map((v) => v.size || v.color || 'Standard')
              : ['Standard'];

            // Find selected events objects
            const matchedEvents = (p.availableEventIds || [])
              .map((tId) => treksList.find((t) => String(t.id) === String(tId)))
              .filter(Boolean);

            setForm({
              name: p.name || '',
              categoryId: p.categoryId || '',
              storeType: p.storeType || 'merchandise',
              saleType: p.saleType || 'purchase',
              basePurchasePrice: p.basePurchasePrice || '',
              baseRentalPrice: p.baseRentalPrice || '',
              securityDeposit: p.securityDeposit || '',
              selectedEvents: matchedEvents,
              eventPricing: p.eventPricing || {},
              eventOffsets: p.eventOffsets || {},
              description: p.description || '',
              specificationsList: specs,
              images: Array.isArray(p.images) ? p.images : [],
              variantsList: vList,
              newVariantInput: '',
              isActive: p.isActive !== false,
              isFeatured: !!p.isFeatured
            });
          }
        } else if (catRes.data.data?.length > 0) {
          setForm((prev) => ({
            ...prev,
            categoryId: catRes.data.data[0].id,
            variantsList: ['UK 6', 'UK 7', 'UK 8', 'UK 9', 'UK 10'],
            specificationsList: [
              { key: 'Special Feature', value: 'Slip Resistant, Water Resistant' },
              { key: 'Closure Type', value: 'Lace-Up' },
              { key: 'Sport Type', value: 'Trekking / Hiking' },
              { key: 'Insole Cushioning', value: 'High Density Foam' }
            ]
          }));
        }
      } catch (err) {
        setError('Failed to load product data.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, isEdit]);

  const handleEventPriceChange = (trekId, priceVal) => {
    setForm((prev) => ({
      ...prev,
      eventPricing: {
        ...prev.eventPricing,
        [trekId]: priceVal
      }
    }));
  };

  const handleEventOffsetChange = (trekId, field, offsetVal) => {
    setForm((prev) => {
      const current = prev.eventOffsets[trekId] || { issueOffset: 0, returnOffset: 4 };
      return {
        ...prev,
        eventOffsets: {
          ...prev.eventOffsets,
          [trekId]: {
            ...current,
            [field]: Number(offsetVal) || 0
          }
        }
      };
    });
  };

  const handleAddSpecRow = () => {
    setForm((prev) => ({
      ...prev,
      specificationsList: [...prev.specificationsList, { key: '', value: '' }]
    }));
  };

  const handleUpdateSpecRow = (index, field, val) => {
    setForm((prev) => {
      const updated = [...prev.specificationsList];
      updated[index][field] = val;
      return { ...prev, specificationsList: updated };
    });
  };

  const handleRemoveSpecRow = (index) => {
    setForm((prev) => ({
      ...prev,
      specificationsList: prev.specificationsList.filter((_, i) => i !== index)
    }));
  };

  const handleAddVariant = () => {
    const val = form.newVariantInput.trim();
    if (!val) return;
    if (form.variantsList.includes(val)) {
      alert('Variant already exists');
      return;
    }
    setForm((prev) => ({
      ...prev,
      variantsList: [...prev.variantsList, val],
      newVariantInput: ''
    }));
  };

  const handleRemoveVariant = (indexToRemove) => {
    setForm((prev) => ({
      ...prev,
      variantsList: prev.variantsList.filter((_, idx) => idx !== indexToRemove)
    }));
  };

  const handleSave = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!form.name.trim()) {
      alert('Please enter a product name');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const token = localStorage.getItem('token') || localStorage.getItem('admin_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      // Filter and build specifications array (format: "Key: Value")
      const specsList = form.specificationsList
        .filter((s) => s.key.trim() || s.value.trim())
        .map((s) => (s.key.trim() ? `${s.key.trim()}: ${s.value.trim()}` : s.value.trim()));

      // Construct variants payload in current reordered sequence
      const variants = (form.variantsList.length > 0 ? form.variantsList : ['Standard']).map((vName) => ({
        size: vName,
        purchasePrice: Number(form.basePurchasePrice) || 0,
        rentalPrice: Number(form.baseRentalPrice) || 0,
        securityDeposit: Number(form.securityDeposit) || 0,
        stockQuantity: 0
      }));

      const availableEventIds = form.selectedEvents.map((t) => t.id);

      const payload = {
        name: form.name,
        categoryId: form.categoryId ? Number(form.categoryId) : null,
        storeType: form.storeType,
        saleType: form.saleType,
        basePurchasePrice: Number(form.basePurchasePrice) || 0,
        baseRentalPrice: Number(form.baseRentalPrice) || 0,
        securityDeposit: Number(form.securityDeposit) || 0,
        availableEventIds,
        eventPricing: form.eventPricing,
        eventOffsets: form.eventOffsets,
        description: form.description,
        specifications: specsList,
        images: form.images,
        isActive: form.isActive,
        isFeatured: form.isFeatured,
        variants
      };

      if (isEdit) {
        await axios.put(`/api/v1/admin/store/products/${id}`, payload, { headers });
      } else {
        await axios.post('/api/v1/admin/store/products', payload, { headers });
      }

      navigate('/store-products');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save product');
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const showPurchasePrice = form.saleType === 'purchase' || form.saleType === 'both';
  const showRentalPrice = form.saleType === 'rental' || form.saleType === 'both';

  return (
    <Box sx={{ p: 3, width: '100%' }}>
      {/* Top Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/store-products')}
            sx={{ borderRadius: 2 }}
          >
            Back to Products
          </Button>
          <Typography variant="h5" fontWeight="bold">
            {isEdit ? `Edit Product: ${form.name}` : 'Add New Product'}
          </Typography>
        </Box>

        <Button
          variant="contained"
          color="primary"
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
          sx={{ borderRadius: 2, px: 3, py: 1 }}
        >
          {saving ? 'Saving...' : 'Save Product'}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {/* Section 1: Basic Information & Pricing */}
        <Paper sx={{ p: 3.5, borderRadius: 2 }} elevation={1}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
            1. Basic Information & Pricing
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Row 1: Product Name (Full width) */}
            <TextField
              label="Product Name"
              required
              fullWidth
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Invincible Waterproof Down Jacket"
            />

            {/* Row 2: Category & Store Type */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5 }}>
              <TextField
                select
                label="Product Category"
                required
                fullWidth
                value={form.categoryId}
                onChange={(e) => setForm({ ...form, categoryId: e.target.value })}
              >
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.name} ({cat.storeType === 'camper_store' ? "Camper's Gear" : 'Merchandise'})
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                label="Store Section"
                required
                fullWidth
                value={form.storeType}
                onChange={(e) => setForm({ ...form, storeType: e.target.value })}
              >
                <MenuItem value="merchandise">Official Merchandise (Open to All)</MenuItem>
                <MenuItem value="camper_store">Camper's Store (Requires Participant Verification)</MenuItem>
                <MenuItem value="both">Both Sections</MenuItem>
              </TextField>
            </Box>

            {/* Row 3: Purchase / Rental Option & Pricing */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2.5 }}>
              <TextField
                select
                label="Sale Type"
                required
                fullWidth
                value={form.saleType}
                onChange={(e) => setForm({ ...form, saleType: e.target.value })}
              >
                <MenuItem value="purchase">Direct Purchase Only</MenuItem>
                <MenuItem value="rental">Rent for Camp Only</MenuItem>
                <MenuItem value="both">Both Purchase & Rental Available</MenuItem>
              </TextField>

              {showPurchasePrice && (
                <TextField
                  label="Purchase Price (₹)"
                  type="number"
                  required
                  fullWidth
                  value={form.basePurchasePrice}
                  onChange={(e) => setForm({ ...form, basePurchasePrice: e.target.value })}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>
                  }}
                />
              )}

              {showRentalPrice && (
                <TextField
                  label="Base Rental Price (₹ / Trek)"
                  type="number"
                  required
                  fullWidth
                  value={form.baseRentalPrice}
                  onChange={(e) => setForm({ ...form, baseRentalPrice: e.target.value })}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>
                  }}
                />
              )}

              {showRentalPrice && (
                <TextField
                  label="Security Deposit (₹ Refundable)"
                  type="number"
                  fullWidth
                  value={form.securityDeposit}
                  onChange={(e) => setForm({ ...form, securityDeposit: e.target.value })}
                  helperText="100% refunded when gear is returned at camp"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>
                  }}
                />
              )}
            </Box>

            {/* Status Toggles */}
            <Box sx={{ display: 'flex', gap: 4, pt: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isActive}
                    onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                    color="success"
                  />
                }
                label="Product Active (Visible in store)"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={form.isFeatured}
                    onChange={(e) => setForm({ ...form, isFeatured: e.target.checked })}
                    color="primary"
                  />
                }
                label="Featured Product (Show on Homepage)"
              />
            </Box>
          </Box>
        </Paper>

        {/* Section 2: Event-Specific Availability & Dynamic Event Pricing (Multiselect) */}
        <Paper sx={{ p: 3.5, borderRadius: 2 }} elevation={1}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
            2. Event-Specific Availability & Dynamic Event Pricing
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Assign this equipment to specific treks/camps. Optionally override the rental/purchase price for each selected event. If left empty, the gear is available for all events at standard price.
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Multiselect Autocomplete for Treks */}
            <Autocomplete
              multiple
              options={allTreks}
              getOptionLabel={(option) => `${option.name} (${option.location || 'Himalayas'})`}
              value={form.selectedEvents}
              isOptionEqualToValue={(option, val) => String(option.id) === String(val.id)}
              onChange={(_, newValue) => setForm({ ...form, selectedEvents: newValue })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Available Events / Treks (Multiselect)"
                  placeholder="Type to search and select treks..."
                />
              )}
            />

            {/* Dynamic Event Pricing & Rental Offsets Table for each selected event */}
            {form.selectedEvents.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
                  Event Pricing & Rental Duration Offsets:
                </Typography>
                
                <Alert severity="info" sx={{ mb: 2, borderRadius: 1.5 }}>
                  <Typography variant="body2" fontWeight="600" sx={{ mb: 0.5 }}>
                    💡 How Date Offsets Work (Real Example):
                  </Typography>
                  <Typography variant="caption" display="block">
                    Suppose a batch runs from <strong>30 Aug to 07 Sep</strong>:
                  </Typography>
                  <Typography variant="caption" display="block">
                    • <strong>Issue Offset = 0</strong> ➔ Gear issued on <strong>Day 1 (30 Aug)</strong> | <strong>Issue Offset = 2</strong> ➔ Gear issued on <strong>Day 3 (01 Sep)</strong> (after train/transit)
                  </Typography>
                  <Typography variant="caption" display="block">
                    • <strong>Return Offset = 4</strong> ➔ Gear returned on <strong>Day 5 (03 Sep)</strong> | <strong>Return Offset = 6</strong> ➔ Gear returned on <strong>Day 7 (05 Sep)</strong> (before camp departure)
                  </Typography>
                </Alert>

                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5 }}>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Event / Trek Name</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', width: '22%' }}>Custom Price (₹)</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', width: '24%' }}>
                          Rental Issue Day Offset
                          <Typography variant="caption" color="text.secondary" display="block">
                            (0 = Day 1, 2 = Day 3)
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold', width: '24%' }}>
                          Rental Return Day Offset
                          <Typography variant="caption" color="text.secondary" display="block">
                            (4 = Day 5, 6 = Day 7)
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {form.selectedEvents.map((trek) => {
                        const offsets = form.eventOffsets[trek.id] || { issueOffset: 0, returnOffset: 4 };
                        return (
                          <TableRow key={trek.id} hover>
                            <TableCell>
                              <Typography variant="subtitle2" fontWeight="bold">
                                {trek.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {trek.location || 'Himalayas'}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <TextField
                                type="number"
                                size="small"
                                fullWidth
                                placeholder={`Default: ₹${form.baseRentalPrice || form.basePurchasePrice || 0}`}
                                value={form.eventPricing[trek.id] || ''}
                                onChange={(e) => handleEventPriceChange(trek.id, e.target.value)}
                                InputProps={{
                                  startAdornment: <InputAdornment position="start">₹</InputAdornment>
                                }}
                              />
                            </TableCell>

                            <TableCell>
                              <TextField
                                type="number"
                                size="small"
                                fullWidth
                                placeholder="0 (Day 1)"
                                value={offsets.issueOffset ?? 0}
                                onChange={(e) => handleEventOffsetChange(trek.id, 'issueOffset', e.target.value)}
                                helperText="e.g. 0 = Day 1 (30 Aug), 2 = Day 3 (01 Sep)"
                                inputProps={{ min: 0 }}
                              />
                            </TableCell>

                            <TableCell>
                              <TextField
                                type="number"
                                size="small"
                                fullWidth
                                placeholder="4 (Day 5)"
                                value={offsets.returnOffset ?? 4}
                                onChange={(e) => handleEventOffsetChange(trek.id, 'returnOffset', e.target.value)}
                                helperText="e.g. 4 = Day 5 (03 Sep), 6 = Day 7 (05 Sep)"
                                inputProps={{ min: 0 }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>
        </Paper>

        {/* Section 3: Product Details & Media */}
        <Paper sx={{ p: 3.5, borderRadius: 2 }} elevation={1}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
            3. Product Details & Media
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.5 }}>
            {/* Row 1: Product Description with Text Editor (Full width) */}
            <Box>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                Product Description
              </Typography>
              <AdminRichTextEditor
                value={form.description}
                onChange={(value) => setForm((p) => ({ ...p, description: value }))}
                placeholder="Write comprehensive product description, highlights, and benefits..."
              />
            </Box>

            {/* Row 2: Technical Specifications in Table Format */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <div>
                  <Typography variant="subtitle2" fontWeight="bold">
                    Technical Specifications Table
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Provide structured key-value specifications (e.g., Special Feature, Closure Type, Material, Insole Cushioning).
                  </Typography>
                </div>

                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddSpecRow}
                  sx={{ textTransform: 'none', borderRadius: 1.5 }}
                >
                  Add Row
                </Button>
              </Box>

              <Table sx={{ border: '1px solid #E2E8F0', borderRadius: 1.5, overflow: 'hidden' }}>
                <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', width: '35%' }}>Specification Feature / Key</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '55%' }}>Specification Detail / Value</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', width: '10%' }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {form.specificationsList.map((spec, sIdx) => (
                    <TableRow key={sIdx}>
                      <TableCell sx={{ p: 1.5 }}>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="e.g. Special Feature, Material, Sole"
                          value={spec.key}
                          onChange={(e) => handleUpdateSpecRow(sIdx, 'key', e.target.value)}
                        />
                      </TableCell>
                      <TableCell sx={{ p: 1.5 }}>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="e.g. Slip Resistant, Waterproof Gore-Tex"
                          value={spec.value}
                          onChange={(e) => handleUpdateSpecRow(sIdx, 'value', e.target.value)}
                        />
                      </TableCell>
                      <TableCell align="center" sx={{ p: 1.5 }}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveSpecRow(sIdx)}
                          disabled={form.specificationsList.length <= 1}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>

            {/* Row 3: Minimal Image Upload Button & Sequence Reordering */}
            <Box>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                Product Images (Drag & drop to change sequence • 1st photo is Main Image)
              </Typography>
              <MultiImageUpload
                value={form.images}
                onChange={(newImages) => setForm({ ...form, images: newImages })}
                uploadImage={async (file) => {
                  const res = await adminAPI.uploadImage(file);
                  return res.data?.data?.url || res.data?.url || '';
                }}
                maxImages={8}
              />
            </Box>

            {/* Row 4: Product Variants (Sizes/Colors) with Drag & Drop Reordering */}
            <Box>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
                Product Variants (Sizes / Options)
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Drag and drop variant pills below using the handle (⠿) to change their display sequence on the product page. Stock is managed from the <strong>Stock & Inventory Management</strong> page.
              </Typography>

              {/* Drag and Drop Variants List */}
              <Box
                ref={variantsListRef}
                sx={{
                  display: 'flex',
                  gap: 1.25,
                  mb: 2,
                  flexWrap: 'wrap',
                  minHeight: 42,
                  p: 1,
                  border: '1px dashed #CBD5E1',
                  borderRadius: 2,
                  bgcolor: '#F8FAFC'
                }}
              >
                {form.variantsList.map((vName, idx) => (
                  <Box
                    key={`${vName}-${idx}`}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      bgcolor: '#FFFFFF',
                      border: '1.5px solid #CBD5E1',
                      borderRadius: '20px',
                      pl: 1,
                      pr: 1.25,
                      py: 0.6,
                      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                      cursor: 'grab',
                      '&:hover': { borderColor: '#EA580C' }
                    }}
                  >
                    <Box
                      className="variant-drag-handle"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        color: '#64748B',
                        cursor: 'grab',
                        '&:hover': { color: '#0F172A' }
                      }}
                    >
                      <DragIndicatorIcon fontSize="small" />
                    </Box>
                    <Typography variant="body2" fontWeight="bold" sx={{ color: '#0F172A', px: 0.5 }}>
                      {vName}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveVariant(idx)}
                      sx={{ p: 0.25, color: '#94A3B8', '&:hover': { color: '#EF4444' } }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                ))}
              </Box>

              <Box sx={{ display: 'flex', gap: 1, maxWidth: 360 }}>
                <TextField
                  size="small"
                  placeholder="e.g. UK 11 or XXL"
                  value={form.newVariantInput}
                  onChange={(e) => setForm({ ...form, newVariantInput: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddVariant();
                    }
                  }}
                  fullWidth
                />
                <Button
                  variant="outlined"
                  onClick={handleAddVariant}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Add Option
                </Button>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
